import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { CalendarDays, X } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";

const formatRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return "Select range";

  const formatDate = (date) =>
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export const DateRangeFilter = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onFetch,
  onClear,
  isFiltered,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative flex items-center">
      <button
        className="flex items-center gap-2 border border-primary-title rounded-md px-3 py-2 text-sm font-nexa bg-white text-primary-dark min-w-[250px] justify-between"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{formatRangeLabel(startDate, endDate)}</span>
        <span className="flex items-center gap-2">
          {isFiltered && (
            <span
              onClick={(event) => {
                event.stopPropagation();
                onClear();
                setOpen(false);
              }}
              className="text-gray-400 hover:text-red-500 transition-all"
            >
              <X size={14} />
            </span>
          )}
          <CalendarDays size={16} />
        </span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg p-4 w-[320px] z-[9999]">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-medium mb-1 text-primary-dark">Start date</p>
              <DatePicker
                selected={startDate}
                onChange={(date) => onStartDateChange(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                className="border border-primary-title rounded-md px-3 py-2 text-sm font-nexa w-full"
              />
            </div>

            <div>
              <p className="text-sm font-medium mb-1 text-primary-dark">End date</p>
              <DatePicker
                selected={endDate}
                onChange={(date) => onEndDateChange(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                className="border border-primary-title rounded-md px-3 py-2 text-sm font-nexa w-full"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                className="flex gap-2 bg-white border border-primary-title text-primary-title px-3 py-2 rounded-md text-sm hover:bg-primary-dark hover:text-white transition-all"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
              >
                Clear
              </button>
              <button
                className="flex gap-2 bg-primary-title text-white px-3 py-2 rounded-md text-sm hover:bg-primary-dark transition-all"
                onClick={() => {
                  onFetch();
                  setOpen(false);
                }}
              >
                Fetch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
